import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const DEFAULT_GPS_SETTINGS = {
  enabled: true,
  teacherAutoCheckIn: true,
  latitude: 6.243810,
  longitude: 6.676280,
  rangeMeters: 15.24,
  accuracyMeters: 20
};

const normalizeGpsSettings = (settings = {}) => ({
  enabled: settings.enabled ?? DEFAULT_GPS_SETTINGS.enabled,
  teacherAutoCheckIn: settings.teacherAutoCheckIn ?? DEFAULT_GPS_SETTINGS.teacherAutoCheckIn,
  latitude: Number(settings.latitude ?? DEFAULT_GPS_SETTINGS.latitude),
  longitude: Number(settings.longitude ?? DEFAULT_GPS_SETTINGS.longitude),
  rangeMeters: Number(settings.rangeMeters ?? DEFAULT_GPS_SETTINGS.rangeMeters),
  accuracyMeters: Number(settings.accuracyMeters ?? DEFAULT_GPS_SETTINGS.accuracyMeters)
});

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const earthRadiusMeters = 6371e3;
  const phi1 = lat1 * Math.PI / 180;
  const phi2 = lat2 * Math.PI / 180;
  const deltaPhi = (lat2 - lat1) * Math.PI / 180;
  const deltaLambda = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
            Math.cos(phi1) * Math.cos(phi2) *
            Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadiusMeters * c;
};

const formatMeters = (value) => `${Math.round(Number(value || 0) * 100) / 100}m`;
const formatRange = (meters) => `${formatMeters(meters)} (${Math.round(Number(meters || 0) * 3.28084)} feet)`;

const TeacherAttendance = () => {
  const navigate = useNavigate();
  const watchIdRef = useRef(null);
  const settingsLoadedRef = useRef(false);

  const [teacher, setTeacher] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('loading');
  const [locationError, setLocationError] = useState('');
  const [distance, setDistance] = useState(null);
  const [withinRange, setWithinRange] = useState(false);
  const [attendanceMarked, setAttendanceMarked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [autoCheckInEnabled, setAutoCheckInEnabled] = useState(true);
  const [gpsSettings, setGpsSettings] = useState(DEFAULT_GPS_SETTINGS);
  const [gpsSettingsLoaded, setGpsSettingsLoaded] = useState(false);

  const gpsEnabled = Boolean(gpsSettings.enabled);
  const teacherAutoCheckInAllowed = Boolean(gpsSettings.enabled && gpsSettings.teacherAutoCheckIn);
  const rangeMeters = Number(gpsSettings.rangeMeters || DEFAULT_GPS_SETTINGS.rangeMeters);
  const accuracyMeters = Number(gpsSettings.accuracyMeters || DEFAULT_GPS_SETTINGS.accuracyMeters);
  const accuracyAcceptable = !gpsEnabled || !location || Number(location.accuracy) <= accuracyMeters;
  const displayName = teacher?.name || [teacher?.firstName, teacher?.lastName].filter(Boolean).join(' ');

  useEffect(() => {
    checkTeacherAuth();
    loadGpsSettings();

    const settingsInterval = setInterval(loadGpsSettings, 30000);
    return () => {
      clearInterval(settingsInterval);
      stopLocationTracking();
    };
  }, []);

  useEffect(() => {
    if (teacher?.id) {
      getTodayAttendance(teacher.id);
    }
  }, [teacher]);

  useEffect(() => {
    if (!teacher) return;

    if (!gpsEnabled) {
      stopLocationTracking();
      setLocationStatus('disabled');
      setLocationError('');
      setDistance(null);
      setWithinRange(false);
      return;
    }

    startLocationTracking();
  }, [teacher, gpsEnabled]);

  useEffect(() => {
    if (!gpsEnabled || !location) return;

    const schoolLat = Number(gpsSettings.latitude);
    const schoolLon = Number(gpsSettings.longitude);

    if (!Number.isFinite(schoolLat) || !Number.isFinite(schoolLon)) {
      setDistance(null);
      setWithinRange(false);
      return;
    }

    const nextDistance = calculateDistance(location.latitude, location.longitude, schoolLat, schoolLon);
    setDistance(nextDistance);
    setWithinRange(nextDistance <= rangeMeters);
  }, [location, gpsSettings.latitude, gpsSettings.longitude, rangeMeters, gpsEnabled]);

  useEffect(() => {
    checkAutoCheckIn();
  }, [autoCheckInEnabled, withinRange, accuracyAcceptable, attendanceMarked, location, todayAttendance, teacher]);

  const checkTeacherAuth = () => {
    const teacherUser = localStorage.getItem('teacherUser');
    if (!teacherUser) {
      navigate('/teacher-login');
      return;
    }

    const teacherData = JSON.parse(teacherUser);
    if (teacherData.role !== 'teacher') {
      navigate('/teacher-login');
      return;
    }

    setTeacher(teacherData);
  };

  const loadGpsSettings = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.ATTENDANCE_GPS_SETTINGS);
      const settings = await response.json();

      if (!response.ok) {
        throw new Error(settings.error || 'Failed to load GPS settings');
      }

      const normalizedSettings = normalizeGpsSettings(settings);
      setGpsSettings(normalizedSettings);
      setGpsSettingsLoaded(true);
      setAutoCheckInEnabled((current) => {
        if (!normalizedSettings.enabled || !normalizedSettings.teacherAutoCheckIn) return false;
        if (!settingsLoadedRef.current) return true;
        return current;
      });
      settingsLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading GPS settings:', error);
      setGpsSettingsLoaded(true);
    }
  };

  const getTodayAttendance = async (teacherId) => {
    if (!teacherId) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${teacherId}/attendance-today`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teacherToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTodayAttendance(data.attendance);
        setAttendanceMarked(!!data.attendance);
      }
    } catch (error) {
      console.error('Error fetching today attendance:', error);
    }
  };

  const startLocationTracking = () => {
    if (!navigator.geolocation) {
      setLocationStatus('unavailable');
      setLocationError('Geolocation is not supported by this browser.');
      return;
    }

    if (watchIdRef.current !== null) return;

    const options = {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 0
    };

    setLocationStatus('loading');
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        handleLocationUpdate(position);
        watchIdRef.current = navigator.geolocation.watchPosition(
          handleLocationUpdate,
          handleLocationError,
          options
        );
      },
      handleLocationError,
      options
    );
  };

  const stopLocationTracking = () => {
    if (watchIdRef.current !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  };

  const handleLocationUpdate = (position) => {
    const { latitude, longitude, accuracy } = position.coords;
    setLocation({ latitude, longitude, accuracy });
    setLocationStatus('granted');
    setLocationError('');
  };

  const handleLocationError = (error) => {
    console.error('Geolocation error:', error);

    if (error.code === error.PERMISSION_DENIED) {
      setLocationStatus('denied');
      setLocationError('Location permission is blocked for this browser.');
      return;
    }

    setLocationStatus('unavailable');
    setLocationError(error.message || 'Unable to get a reliable location.');
  };

  const checkAutoCheckIn = () => {
    if (
      !gpsEnabled ||
      !autoCheckInEnabled ||
      !withinRange ||
      !accuracyAcceptable ||
      attendanceMarked ||
      !location ||
      !teacher ||
      loading
    ) {
      return;
    }

    const now = new Date();
    const currentTime = now.toTimeString().split(' ')[0];
    const currentDate = now.toISOString().split('T')[0];

    markAttendance('present', currentDate, currentTime, true);
  };

  const markAttendance = async (status = 'present', date = null, time = null, isAuto = false) => {
    if (!teacher || loading) return;

    if (gpsEnabled) {
      if (locationStatus !== 'granted' || !location) {
        alert('Location access is required for GPS attendance.');
        return;
      }

      if (!accuracyAcceptable) {
        alert(`GPS accuracy is ${Math.round(location.accuracy)}m. Required accuracy is ${formatMeters(accuracyMeters)} or better.`);
        return;
      }

      if (!withinRange) {
        alert(`Location validation failed. You are ${Math.round(distance || 0)}m from school. Required range is ${formatMeters(rangeMeters)}.`);
        return;
      }
    }

    setLoading(true);

    try {
      const attendanceData = {
        date: date || new Date().toISOString().split('T')[0],
        status,
        checkInTime: time || new Date().toTimeString().split(' ')[0],
        notes: isAuto ? 'Auto-checked in via location' : 'Manual check-in',
        checkInMethod: isAuto ? 'auto_location' : 'manual',
        autoChecked: isAuto
      };

      if (gpsEnabled && location) {
        attendanceData.location = location;
      }

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${teacher.id}/attendance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(attendanceData)
      });

      const result = await response.json();

      if (response.ok) {
        setAttendanceMarked(true);
        setTodayAttendance(result.attendance);
        alert(isAuto ? 'Auto check-in completed. You are within school premises.' : 'Attendance marked successfully.');
      } else {
        alert(`Failed to mark attendance: ${result.error}`);
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Failed to mark attendance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleAutoCheckIn = () => {
    if (!teacherAutoCheckInAllowed) return;
    setAutoCheckInEnabled((current) => !current);
  };

  const getMarkDisabledReason = () => {
    if (!gpsEnabled) return '';
    if (locationStatus !== 'granted') return 'Location access required to mark attendance.';
    if (!accuracyAcceptable && location) return `GPS accuracy must be ${formatMeters(accuracyMeters)} or better. Current accuracy is ${Math.round(location.accuracy)}m.`;
    if (!withinRange) return `You are ${Math.round(distance || 0)}m from school. Required range is ${formatMeters(rangeMeters)}.`;
    return '';
  };

  const markDisabledReason = getMarkDisabledReason();
  const canMarkAttendance = !loading && (!gpsEnabled || (locationStatus === 'granted' && withinRange && accuracyAcceptable));

  if (!teacher) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading teacher attendance...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-blue-900 mb-2">Teacher Attendance</h1>
            <p className="text-gray-600">Welcome, {displayName || teacher.email}</p>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Location Status</h2>

            {!gpsSettingsLoaded && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <span className="text-blue-800">Loading GPS attendance settings...</span>
              </div>
            )}

            {gpsSettingsLoaded && !gpsEnabled && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <span className="font-medium text-slate-800">GPS attendance is disabled by admin</span>
                <p className="text-sm text-slate-600 mt-1">Teacher attendance can be marked without location validation.</p>
              </div>
            )}

            {gpsEnabled && locationStatus === 'loading' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                  <span className="text-blue-800">Getting your location...</span>
                </div>
              </div>
            )}

            {gpsEnabled && locationStatus === 'granted' && location && (
              <div className={`border rounded-lg p-4 ${withinRange && accuracyAcceptable ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-medium ${withinRange && accuracyAcceptable ? 'text-green-800' : 'text-red-800'}`}>
                    {withinRange ? 'Within School Range' : 'Outside School Range'}
                  </span>
                  <span className="text-sm text-gray-600">
                    Accuracy: {Math.round(location.accuracy)}m
                  </span>
                </div>
                {!accuracyAcceptable && (
                  <div className="text-sm text-red-700 mb-2">
                    GPS accuracy is too low. Required accuracy is {formatMeters(accuracyMeters)} or better.
                  </div>
                )}
                <div className="text-sm text-gray-600">
                  Distance from school: {distance !== null ? `${Math.round(distance)} meters` : 'Calculating...'}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  School coordinates: {Number(gpsSettings.latitude).toFixed(6)}, {Number(gpsSettings.longitude).toFixed(6)} | Range: {formatRange(rangeMeters)}
                </div>
              </div>
            )}

            {gpsEnabled && locationStatus === 'denied' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <span className="text-red-800">Location access denied</span>
                <p className="text-sm text-red-700 mt-2">{locationError || 'Please enable location permissions in your browser settings and refresh the page.'}</p>
              </div>
            )}

            {gpsEnabled && locationStatus === 'unavailable' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <span className="text-yellow-800">Geolocation unavailable</span>
                {locationError && <p className="text-sm text-yellow-700 mt-2">{locationError}</p>}
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={autoCheckInEnabled}
                onChange={toggleAutoCheckIn}
                disabled={!teacherAutoCheckInAllowed}
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-gray-700 font-medium">Enable Auto Check-in</span>
            </label>
            <p className="text-sm text-gray-600 mt-1">
              {teacherAutoCheckInAllowed
                ? `Automatically check in when within ${formatRange(rangeMeters)} of school.`
                : 'Auto check-in is disabled in admin GPS settings.'}
            </p>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Today's Attendance</h2>

            {todayAttendance ? (
              <div className="bg-gray-50 border rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-700">Status:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-sm ${
                      todayAttendance.status === 'present'
                        ? 'bg-green-100 text-green-800'
                        : todayAttendance.status === 'late'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {todayAttendance.status}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Check-in:</span>
                    <span className="ml-2 text-gray-900">{todayAttendance.checkInTime}</span>
                  </div>
                  {todayAttendance.checkOutTime && (
                    <div>
                      <span className="font-medium text-gray-700">Check-out:</span>
                      <span className="ml-2 text-gray-900">{todayAttendance.checkOutTime}</span>
                    </div>
                  )}
                  {todayAttendance.hoursWorked && (
                    <div>
                      <span className="font-medium text-gray-700">Hours:</span>
                      <span className="ml-2 text-gray-900">{todayAttendance.hoursWorked}</span>
                    </div>
                  )}
                </div>
                {todayAttendance.autoChecked && (
                  <div className="mt-2 text-sm text-blue-600">
                    Auto-checked in via location
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <span className="text-yellow-800">No attendance marked for today</span>
              </div>
            )}
          </div>

          {!attendanceMarked && (
            <div className="text-center">
              <button
                onClick={() => markAttendance('present')}
                disabled={!canMarkAttendance}
                className={`px-8 py-3 rounded-lg font-medium text-white ${
                  !canMarkAttendance
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } transition duration-300`}
              >
                {loading ? 'Marking Attendance...' : 'Mark Present'}
              </button>
              {markDisabledReason && (
                <p className="text-sm text-red-600 mt-2">
                  {markDisabledReason}
                </p>
              )}
            </div>
          )}

          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/teacher-dashboard')}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherAttendance;
